/**
 * Landing hero organism wrapping IdeaChatInput with analytics and auth routing.
 * The onboarding-chat handoff (session push → /onboarding/complete, anon-project
 * fast path, sign-up redirect) lives in `useLandingChatHandoff`, shared with the
 * partner landers so completion is identical by construction.
 * The logged-out layout is "classic" (astronaut) or "v2" (landing-home-v2:
 * gradient band + capability cards that send a starter message to the chat).
 * Supports dependency injection for Storybook/tests (useUser, useRouter overrides).
 */
"use client";

import { useEffect, useState, useRef, useCallback, type ReactNode } from "react";
import Image from "next/image";
import IdeaChatInput, {
  type LandingStarterSource,
} from "@/components/molecules/landing/IdeaChatInput";
import AnonProjectResumeNotice from "@/components/molecules/landing/AnonProjectResumeNotice";
import {
  LandingCapabilityCards,
  type LandingCapabilityCard,
} from "@/components/molecules/landing/LandingCapabilityCards";
import {
  trackLandingCapabilityCardClicked,
  trackLandingPageViewed,
} from "@/lib/analytics/client";
import type { LandingPageSlug } from "@/lib/analytics/events";
import { useLandingChatHandoff } from "@/hooks/landing/useLandingChatHandoff";

export interface LandingHeroProps {
  className?: string;
  /** User's first name for personalized greeting (logged-in only) */
  firstName?: string;
  /** When true, show IdeaChatInput in the logged-in hero (e.g. after clicking "New project"). Default true. */
  showIdeaInputWhenSignedIn?: boolean;
  /** Optional override for Clerk `useUser`, used in Storybook/tests. */
  useUserImpl?: () => { isSignedIn: boolean | null | undefined };
  /** Optional override for Next.js `useRouter`, used in Storybook/tests. */
  useRouterImpl?: () => { push: (path: string) => void };
  /** Optional navigation handler used in place of Next.js router. */
  onNavigate?: (path: string) => void;
  /** Standard landing slug used to derive shared lander event names. */
  landingPageSlug?: LandingPageSlug;
  /**
   * LEV-1594: when set, the visitor already has a live anonymous project.
   * Renders a resume notice with a link to this project INSTEAD of the
   * onboarding chat input. Only applies to the logged-out branch.
   */
  existingAnonProjectId?: string;
  /**
   * Logged-out layout. "v2" (landing-home-v2) is the gradient hero with
   * capability cards; the caller owns the flag. The signed-in branch ignores it.
   */
  variant?: "classic" | "v2";
  /**
   * A starter sent from outside the hero (the Training topic chips). Each new
   * `nonce` sends once, sharing the chat's request sequence with the cards.
   * v2 only.
   */
  externalSendRequest?: { text: string; nonce: number; source: LandingStarterSource };
}

// Near-white at top-center, warming to pink/peach at the sides and bottom.
const V2_HERO_BACKGROUND = [
  "radial-gradient(ellipse 45% 70% at 0% 100%, rgba(255,196,170,0.55), transparent 70%)",
  "radial-gradient(ellipse 45% 70% at 100% 100%, rgba(255,160,190,0.55), transparent 70%)",
  "radial-gradient(ellipse 75% 85% at 50% 15%, #F7F8FA 45%, #FCE4E8 72%, #F9C4CF 100%)",
].join(", ");

export default function LandingHero({
  className,
  firstName,
  showIdeaInputWhenSignedIn = true,
  useUserImpl,
  useRouterImpl,
  onNavigate,
  landingPageSlug = "home",
  existingAnonProjectId,
  variant = "classic",
  externalSendRequest,
}: LandingHeroProps) {
  const { handleComplete, isSignedIn, anonymousProjectsFlagLoading } =
    useLandingChatHandoff({ landingPageSlug, useUserImpl, useRouterImpl, onNavigate });
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [sendRequest, setSendRequest] = useState<{
    text: string;
    nonce: number;
    source: LandingStarterSource;
  }>();
  const handleCapabilitySelect = useCallback((card: LandingCapabilityCard) => {
    trackLandingCapabilityCardClicked({ card: card.id });
    setSendRequest((prev) => ({
      text: card.starter,
      nonce: (prev?.nonce ?? 0) + 1,
      source: "capability_card",
    }));
  }, []);
  const externalText = externalSendRequest?.text;
  const externalNonce = externalSendRequest?.nonce;
  const externalSource = externalSendRequest?.source;
  useEffect(() => {
    if (externalText === undefined || externalSource === undefined) return;
    setSendRequest((prev) => ({
      text: externalText,
      nonce: (prev?.nonce ?? 0) + 1,
      source: externalSource,
    }));
  }, [externalText, externalNonce, externalSource]);
  // Once-guard: the view effect's deps include
  // anonymousProjectsFlagLoading, so it re-runs on settle; without this the
  // event would fire twice if both landingPageSlug and the flag change.
  const viewEventFiredRef = useRef(false);

  useEffect(() => {
    // Trigger hero animation on mount
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, [landingPageSlug]);

  // The view event must carry $feature/anonymous-projects because the rollout
  // dashboard's funnels use it as the arm on step 1; firing before flags settle
  // drops the visitor from both arms.
  useEffect(() => {
    if (anonymousProjectsFlagLoading || viewEventFiredRef.current) return;
    viewEventFiredRef.current = true;
    trackLandingPageViewed(landingPageSlug);
  }, [landingPageSlug, anonymousProjectsFlagLoading]);

  // Logged-in variant: Welcome back; optionally show IdeaChatInput (or rely on "New project" card)
  if (isSignedIn) {
    return (
      <section className={className}>
        <div className="flex flex-col items-center pt-8 px-4 md:px-[100px]">
          <div className="h-[60px] md:h-[80px]" /> {/* Spacer for nav */}

          <div
            className="flex flex-col items-center gap-9 w-full max-w-[720px]"
            style={{
              opacity: heroLoaded ? 1 : 0,
              transform: heroLoaded ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
            }}
          >
            <div className="text-center">
              <h1 className="text-[36px] md:text-[48px] text-landing-dark tracking-[-0.96px] leading-[1.1] mb-5">
                Welcome back{firstName ? `, ${firstName}` : ""}!
              </h1>
              <p className="text-lg md:text-xl text-landing-secondary leading-[1.4]">
                Let&apos;s get cracking.
              </p>
            </div>

            {showIdeaInputWhenSignedIn && (
              <IdeaChatInput
                onComplete={handleComplete}
                landingPageSlug={landingPageSlug}
              />
            )}
          </div>
        </div>
      </section>
    );
  }

  const contentRevealStyle = {
    opacity: heroLoaded ? 1 : 0,
    transform: heroLoaded ? "translateY(0)" : "translateY(30px)",
    transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
  };

  const resumeNotice = existingAnonProjectId ? (
    /* LEV-1594: returning visitor with a live anonymous project */
    <AnonProjectResumeNotice
      projectId={existingAnonProjectId}
      landingPageSlug={landingPageSlug}
      textTheme="dark"
      trackImpression={isSignedIn === false}
    />
  ) : null;

  const renderGreeting = (subtitle: ReactNode) => (
    <div className="text-center">
      <h1 className="text-[36px] md:text-[48px] text-landing-dark tracking-[-0.96px] leading-[1.1] mb-5">
        Hey, I&apos;m Lev.
      </h1>
      <p className="text-lg md:text-xl text-landing-secondary leading-[1.4]">{subtitle}</p>
    </div>
  );

  if (variant === "v2") {
    return (
      <section className={className}>
        <div
          className="relative overflow-hidden"
          style={{ backgroundImage: V2_HERO_BACKGROUND }}
        >
          <div className="relative z-card-above flex flex-col items-center pt-8 pb-12 md:pb-14 px-4 md:px-[100px]">
            <div className="h-[60px] md:h-[80px]" /> {/* Spacer for nav */}
            <div className="mb-8 md:mb-14" />

            <div
              className="flex flex-col items-center gap-9 w-full max-w-[720px]"
              style={contentRevealStyle}
            >
              {resumeNotice ?? (
                <>
                  {renderGreeting(
                    <>
                      <span className="block">
                        Your AI co-founder, here to help you build the next big thing.
                      </span>
                      <span className="block">I&apos;m built and trained to help with:</span>
                    </>,
                  )}

                  <LandingCapabilityCards onSelect={handleCapabilitySelect} />

                  <IdeaChatInput
                    onComplete={handleComplete}
                    landingPageSlug={landingPageSlug}
                    sendRequest={sendRequest}
                    hideGreeting
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Logged-out variant: Full hero with astronaut imagery
  return (
    <section className={className}>
      <div className="relative min-h-[85vh] md:min-h-screen overflow-hidden">
        {/* Astronaut Section with Planet - positioned at bottom */}
        <div
          className="absolute left-0 w-full pointer-events-none"
          style={{
            top: "55%",
            bottom: "0",
            opacity: heroLoaded ? 1 : 0,
            transform: heroLoaded ? "translateY(0)" : "translateY(40px)",
            transition: "opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s",
          }}
        >
          {/* Planet Image - Behind Astronaut */}
          <div className="absolute w-[140%] left-1/2 -translate-x-1/2 bottom-[-30%]">
            <Image
              src="/landing/Orange Purple Planet.png"
              alt=""
              width={1400}
              height={700}
              className="w-full h-auto"
              priority
            />
          </div>
          {/* Astronaut Image - Centered */}
          <div className="absolute w-[70%] md:w-[60%] left-1/2 -translate-x-1/2 bottom-0">
            <Image
              src="/landing/Header Astro.png"
              alt=""
              width={1000}
              height={800}
              className="w-full h-auto"
              priority
            />
          </div>
        </div>

        {/* Hero Content - Upper Center */}
        <div className="relative z-10 flex flex-col items-center pt-8 px-4 md:px-[100px]">
          <div className="h-[60px] md:h-[80px]" /> {/* Spacer for nav */}
          <div className="mb-[80px] md:mb-[152px]" />

          <div
            className="flex flex-col items-center gap-9 w-full max-w-[720px]"
            style={contentRevealStyle}
          >
            {resumeNotice ?? (
              <>
                {renderGreeting("Your AI co-founder. I'm here to help elevate your startup.")}

                <IdeaChatInput
                  onComplete={handleComplete}
                  landingPageSlug={landingPageSlug}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
